// Ingress AB-test trace-id storage helpers.
//
// The ingress gateway appends a fresh `?ingress_trace_id=<UUIDv7>` to every
// redirect — one id per ad-click visit. Attribution needs BOTH the id of the
// FIRST landing this browser ever made (origin) and the id of the most recent
// landing that carried one (current): a returning visitor who clicks a second
// ad arrives with a new id, and origin/latest diverging is exactly that
// signal. A visit without the param (reload, direct visit, abandoned-cart
// link) keeps both values.
//
// The record must survive the landing-page reset (`clearIfLandingPage` →
// `clearPersistedData` wipes the pipe's `pipe_store_*` localStorage key and
// all in-memory variables — and an ingress redirect lands ON the landing
// page). So, like the cookie-consent decision (@render/lib/cookieConsent),
// the record lives in its OWN localStorage key — NOT under `pipe_store_*` —
// scoped per distribution so parallel funnels on one host don't cross-talk.
// The `var.system.originIngressTraceId` / `var.system.currentIngressTraceId`
// runtime variables are reactive MIRRORS re-asserted from here by
// `seedOriginDefaults` on every load (the isRtl/cookie-consent pattern); this
// localStorage record is the source of truth.

export const INGRESS_TRACE_ID_QUERY_PARAM = 'ingress_trace_id'

/** Variable keys (without the `var.` prefix) mirrored from the stored record. */
export const ORIGIN_INGRESS_TRACE_ID_VAR_KEY = 'system.originIngressTraceId'
export const CURRENT_INGRESS_TRACE_ID_VAR_KEY = 'system.currentIngressTraceId'

export interface IngressTraceIds {
  origin?: string
  current?: string
}

const storageKeyFor = (distributionPath: string) => `ingress_trace_${distributionPath}`

/** Read the persisted record. Missing/garbage/unparseable → {}. Never throws. */
export function readStoredIngressTraceIds(distributionPath: string | null): IngressTraceIds {
  if (typeof window === 'undefined' || !distributionPath) return {}
  try {
    const raw = window.localStorage?.getItem(storageKeyFor(distributionPath))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<IngressTraceIds>
    return {
      origin: typeof parsed?.origin === 'string' && parsed.origin ? parsed.origin : undefined,
      current: typeof parsed?.current === 'string' && parsed.current ? parsed.current : undefined,
    }
  } catch {
    return {}
  }
}

/** Persist the record. SSR-safe and never throws (blocked storage → next visit re-derives from the URL). */
export function writeStoredIngressTraceIds(
  distributionPath: string | null,
  ids: IngressTraceIds,
): void {
  if (typeof window === 'undefined' || !distributionPath) return
  try {
    window.localStorage?.setItem(storageKeyFor(distributionPath), JSON.stringify(ids))
  } catch {
    // ignore — degrades to session-only mirrors in pipe variables
  }
}

/** Extract the trace id carried by a query string, if any. Never throws. */
export function readUrlIngressTraceId(search: string): string | null {
  try {
    return new URLSearchParams(search).get(INGRESS_TRACE_ID_QUERY_PARAM)
  } catch {
    return null
  }
}

/**
 * Merge the persisted record with the id carried by the current URL:
 * origin seeds once (kept forever after the first landing that carried the
 * param), current follows every landing that carries one, and a URL without
 * the param leaves the stored record untouched.
 */
export function resolveIngressTraceIds(
  stored: IngressTraceIds,
  urlTraceId: string | null,
): IngressTraceIds {
  if (!urlTraceId) return stored
  return { origin: stored.origin ?? urlTraceId, current: urlTraceId }
}
